import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ModeProvider } from "./context/ModeContext";
import { SettingsProvider, useSettings } from "./context/SettingsContext";
import { ThemeProvider } from "./context/ThemeContext";
import AppLayout from "./components/AppLayout";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Terminal from "./pages/Terminal";
import History from "./pages/History";
import WalletPage from "./pages/Wallet";
import Bots from "./pages/Bots";
import VendorProfile from "./pages/VendorProfile";
import Admin from "./pages/Admin";
import Kyc from "./pages/Kyc";
import Referrals from "./pages/Referrals";
import News from "./pages/News";
import Support from "./pages/Support";
import Security from "./pages/Security";
import Notifications from "./pages/Notifications";
import Maintenance from "./pages/Maintenance";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Page from "./pages/Page";
import WhatsAppWidget from "./components/WhatsAppWidget";
import Tracker from "./components/Tracker";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-fx-base">
      <div className="flex flex-col items-center gap-4">
        <div className="h-10 w-10 rounded-full border-2 border-brandblue border-t-transparent animate-spin" />
        <p className="text-fx-s3 text-sm font-mono tracking-widest">LOADING FX CLOUD</p>
      </div>
    </div>
  );
}

function ProtectedShell() {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" replace />;
  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}

function GuestOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user && typeof user === "object") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  const { general, loaded } = useSettings();

  const isAdmin = user && typeof user === "object" && ["admin", "super_admin"].includes(user.role);
  if (loaded && general.maintenance_mode && !isAdmin && location.pathname !== "/login") {
    // wait for auth to resolve so admins aren't shown the maintenance page
    if (user === null) return <Loader />;
    return <Maintenance />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/p/:slug" element={<Page />} />
      <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
      <Route element={<ProtectedShell />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/terminal" element={<Terminal />} />
        <Route path="/history" element={<History />} />
        <Route path="/wallet" element={<WalletPage />} />
        <Route path="/bots" element={<Bots />} />
        <Route path="/vendors/:slug" element={<VendorProfile />} />
        <Route path="/kyc" element={<Kyc />} />
        <Route path="/referrals" element={<Referrals />} />
        <Route path="/news" element={<News />} />
        <Route path="/support" element={<Support />} />
        <Route path="/security" element={<Security />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/admin" element={<Admin />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ModeProvider>
            <SettingsProvider>
              <ThemeProvider>
                <Tracker />
                <AppRouter />
                <WhatsAppWidget />
                <Toaster position="top-right" theme="dark" />
              </ThemeProvider>
            </SettingsProvider>
          </ModeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
